import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import TabBar from '../../components/common/TabBar/TabBar';
import { useAuth } from '../../contexts/AuthContext';
import {
  canAccessCommercialDashboard,
  canAccessDashboard,
  canAccessPerformanceSupportDashboard,
  canAccessRecentBodyPhotosDashboard,
  canAccessSensitiveStudentsDashboard,
} from '../../utils/permissions';
import {
  HOME_DASHBOARD_ITEMS,
  getHomeDashboardKey,
  type HomeDashboardKey,
} from './homeNavigation';
import PaymentsDuePanel from './PaymentsDuePanel';
import PerformanceSupportPanel from './PerformanceSupportPanel';
import PaymentsReceivedPanel from './PaymentsReceivedPanel';
import RecentBodyPhotosPanel from './RecentBodyPhotosPanel';
import SalesGoalsPanel from './SalesGoalsPanel';
import SensitiveStudentsPanel from './SensitiveStudentsPanel';
import SupportDashboard from './SupportDashboard';
import styles from './Dashboard.module.css';

const DASHBOARD_HEADER: Record<HomeDashboardKey, { title: string; subtitle?: string }> = {
  delinquency: {
    title: 'Inadimplentes',
    subtitle: 'Alunas ativas sem pagamento após o período do plano (mensal: mais de 40 dias)',
  },
  due: {
    title: 'A vencer/vencidos',
    subtitle: 'Mensal a partir de 25 dias; planos de 2/3/6/12 meses entram 7 dias antes do vencimento',
  },
  goals: {
    title: 'Dashboard comercial',
    subtitle: 'Leads, vendas, receita contratada e meta do mês',
  },
  payments: {
    title: 'Pagamentos',
    subtitle: 'Alunas ativas com conta de pagamento e assinatura em aberto que pagaram no período',
  },
  performanceSupport: {
    title: 'Suporte da performance',
    subtitle: 'Alunas com problema anotado para a performance',
  },
  recentBodyPhotos: {
    title: 'Fotos recentes',
    subtitle: 'Pastas de fotos corporais para avaliar; marcadas hoje saem da lista amanhã',
  },
  sensitiveStudents: {
    title: 'Alunas sensíveis',
    subtitle: 'Períodos sensíveis abertos e encerrados, com dias de sensibilidade',
  },
};

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { roles } = useAuth();
  const activeKey = getHomeDashboardKey(location.pathname);
  const header = DASHBOARD_HEADER[activeKey];
  const tabs = useMemo(
    () => HOME_DASHBOARD_ITEMS.filter((item) => {
      if (item.key === 'goals') {
        return canAccessCommercialDashboard(roles);
      }
      if (item.key === 'performanceSupport') {
        return canAccessPerformanceSupportDashboard(roles);
      }
      if (item.key === 'recentBodyPhotos') {
        return canAccessRecentBodyPhotosDashboard(roles);
      }
      if (item.key === 'sensitiveStudents') {
        return canAccessSensitiveStudentsDashboard(roles);
      }
      return canAccessDashboard(roles);
    }),
    [roles],
  );

  return (
    <div>
      <PageHeader title={header.title} subtitle={header.subtitle} />
      {tabs.length > 1 && (
        <TabBar
          tabs={tabs}
          activeKey={activeKey}
          onTabChange={(key) => {
            const item = tabs.find((entry) => entry.key === key);
            if (item && item.path !== location.pathname) {
              navigate(item.path);
            }
          }}
        />
      )}
      <div className={styles.tabpage}>
        {activeKey === 'goals' && <SalesGoalsPanel />}
        {activeKey === 'payments' && <PaymentsReceivedPanel />}
        {activeKey === 'due' && <PaymentsDuePanel />}
        {activeKey === 'delinquency' && <SupportDashboard />}
        {activeKey === 'performanceSupport' && <PerformanceSupportPanel />}
        {activeKey === 'recentBodyPhotos' && <RecentBodyPhotosPanel />}
        {activeKey === 'sensitiveStudents' && <SensitiveStudentsPanel />}
      </div>
    </div>
  );
};

export default Dashboard;
